import { useEffect, useMemo, useState } from 'react'

import { activeTabFromUrl, navigateTo } from '../../../common/frontend/route-utils.js'
import { buildEmployeeFilterOptions } from '../../components/portal-people-filters/people-filter-options.js'
import type {
    FilterOptions,
    PeopleFilters,
    SortState,
} from '../../components/portal-people-filters/types.js'
import { emptyPortalPermissions, hasPerm } from '../../portal-role.js'
import { View } from '../../types.js'
import { loadPortalRoleIfNeeded } from '../../utils/load-utils.js'
import {
    createEmployee,
    fetchCompanies,
    fetchEmployees,
    fetchLocations,
    fetchManagers,
    fetchPeopleEvents,
    fetchTeams,
    type UpcomingEvent,
} from './utils/portal-api.js'
import { peopleFilterRefsCurrentlyActive, type PeopleTab } from './views/people/people-types.js'
import type { CreateEmployeeData, EmployeeListItem } from './views/people/people-view.js'
import { PeopleView } from './views/people/people-view.js'

export const PeopleRoute = ({ view }: { view: View }) => {
    const lang = view.state.lang

    const portalRole = loadPortalRoleIfNeeded(view).data
    const permissions = portalRole?.permissions ?? emptyPortalPermissions()
    const canAddMembers = hasPerm(permissions, 'profile', 'add_members')
    const canSeeOffboarded = hasPerm(permissions, 'profile', 'see_offboarded_users')

    // Tabs — persisted in the URL (?tab=) so they survive refresh and are shareable.
    const activeTab = activeTabFromUrl({ param: 'tab', fallback: 'active' }) as PeopleTab
    // Offboarded needs `see_offboarded_users`; coerce back to Active for everyone else.
    const effectiveTab: PeopleTab =
        activeTab === 'offboarded' && !canSeeOffboarded ? 'active' : activeTab
    const listStatus = effectiveTab === 'offboarded' ? 'inactive' : 'active'

    // People directory state
    const [employees, setEmployees] = useState<EmployeeListItem[]>([])
    // Active-employee total drives the "Active" tab badge; it's only refreshed
    // on active-status fetches so it stays correct while viewing Offboarded.
    const [activeCount, setActiveCount] = useState(0)
    const [isLoadingPeople, setIsLoadingPeople] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [filters, setFilters] = useState<PeopleFilters>({})
    const [sort, setSort] = useState<SortState>({ field: 'hire_date', dir: 'desc' })
    const [peopleRefreshKey, setPeopleRefreshKey] = useState(0)

    // Events state
    const [events, setEvents] = useState<UpcomingEvent[]>([])
    const [isLoadingEvents, setIsLoadingEvents] = useState(false)

    // Reference / filter-option data
    const [companies, setCompanies] = useState<{ id: number; name: string }[]>([])
    // `teams`/`managers` are the full lists (create form, Offboarded tab
    // filters); the `active*` variants hold only currently active teams and
    // managers for the Active/Events tab filters (TASK-308), matching the
    // Who's-away calendar so no offered filter can yield an empty roster.
    const [teams, setTeams] = useState<{ id: number; name: string }[]>([])
    const [activeTeams, setActiveTeams] = useState<{ id: number; name: string }[]>([])
    const [locations, setLocations] = useState<
        { id: number; name: string; country_code: string }[]
    >([])
    const [managers, setManagers] = useState<
        { id: number; first_name: string; last_name: string }[]
    >([])
    const [activeManagers, setActiveManagers] = useState<
        { id: number; first_name: string; last_name: string }[]
    >([])

    const [showCreateForm, setShowCreateForm] = useState(false)

    // Org chart — full active roster, fetched lazily on first open and cached.
    const [showOrgChart, setShowOrgChart] = useState(false)
    const [orgChartEmployees, setOrgChartEmployees] = useState<EmployeeListItem[]>([])

    const onToggleOrgChart = (open: boolean) => {
        setShowOrgChart(open)
        if (open && orgChartEmployees.length === 0) {
            void (async () => {
                const res = await fetchEmployees({ status: 'active', limit: 500 })
                if (res.ok) setOrgChartEmployees(res.data)
            })()
        }
    }

    // Detect ?action=create on people page
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (params.get('action') === 'create' && canAddMembers) {
            setShowCreateForm(true)
            window.history.replaceState({}, '', '/people')
        }
    }, [canAddMembers])

    // Load reference data (companies/teams/locations/managers) for filters + create form
    useEffect(() => {
        void (async () => {
            const [compRes, teamRes, activeTeamRes, locRes, mgrRes, activeMgrRes] =
                await Promise.all([
                    fetchCompanies(),
                    fetchTeams(),
                    fetchTeams({ currentlyActive: true }),
                    fetchLocations(),
                    fetchManagers(),
                    fetchManagers({ currentlyActive: true }),
                ])
            if (compRes.ok) setCompanies(compRes.data)
            if (teamRes.ok) setTeams(teamRes.data)
            if (activeTeamRes.ok) setActiveTeams(activeTeamRes.data)
            if (locRes.ok) setLocations(locRes.data)
            if (mgrRes.ok) setManagers(mgrRes.data)
            if (activeMgrRes.ok) setActiveManagers(activeMgrRes.data)
        })()
    }, [])

    // Load people list
    useEffect(() => {
        setIsLoadingPeople(true)
        void (async () => {
            try {
                const result = await fetchEmployees({
                    search: searchQuery,
                    sort: sort.field === 'leave_type' ? 'last_name' : sort.field,
                    dir: sort.dir,
                    companyId: filters.companyId,
                    teamId: filters.teamId,
                    locationId: filters.locationId,
                    countryCode: filters.countryCode,
                    managerId: filters.managerId,
                    status: listStatus,
                })
                if (result.ok) {
                    setEmployees(result.data)
                    if (listStatus === 'active') setActiveCount(result.total)
                }
            } finally {
                setIsLoadingPeople(false)
            }
        })()
    }, [searchQuery, peopleRefreshKey, filters, sort, listStatus])

    // Load events (once; cheap and powers the tab badge)
    useEffect(() => {
        setIsLoadingEvents(true)
        void (async () => {
            try {
                const res = await fetchPeopleEvents(30)
                if (res.ok) setEvents(res.data)
            } finally {
                setIsLoadingEvents(false)
            }
        })()
    }, [peopleRefreshKey])

    const currentlyActiveRefs = peopleFilterRefsCurrentlyActive(effectiveTab)
    const filterTeams = currentlyActiveRefs ? activeTeams : teams
    const filterManagers = currentlyActiveRefs ? activeManagers : managers
    const filterOptions: FilterOptions = useMemo(
        () =>
            buildEmployeeFilterOptions(lang, {
                companies,
                teams: filterTeams,
                locations,
                managers: filterManagers,
            }),
        [companies, filterTeams, locations, filterManagers, lang],
    )

    return (
        <PeopleView
            view={view}
            lang={lang}
            canAddMembers={canAddMembers}
            canSeeOffboarded={canSeeOffboarded}
            activeTab={effectiveTab}
            onTabChange={(tab) => navigateTo(`/people?tab=${tab}`)}
            activeCount={activeCount}
            eventsCount={events.length}
            employees={employees}
            isLoading={isLoadingPeople}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            filters={filters}
            onFiltersChange={setFilters}
            filterOptions={filterOptions}
            sort={sort}
            onSortChange={setSort}
            events={events}
            isLoadingEvents={isLoadingEvents}
            showCreateForm={showCreateForm}
            onToggleCreateForm={setShowCreateForm}
            onCreateEmployee={async (data: CreateEmployeeData) => {
                const res = await createEmployee({ ...data })
                if (!res.ok) return false
                setShowCreateForm(false)
                setSearchQuery('')
                setPeopleRefreshKey((k) => k + 1)
                return true
            }}
            companies={companies}
            teams={teams}
            showOrgChart={showOrgChart}
            onToggleOrgChart={onToggleOrgChart}
            orgChartEmployees={orgChartEmployees}
        />
    )
}
